import React from "react";
import styled from "styled-components";
import { MediaCard } from "./MediaCard";
import { Movie, TVShow } from "../../types";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../../store/store";
import {
  toggleMovieWatchlist,
  toggleShowWatchlist,
} from "../../store/slices/mediaSlice";

interface MediaGridProps {
  items: (Movie | TVShow)[];
  type: "movie" | "tv";
  showRemoveButton?: boolean;
  renderItem?: (item: Movie | TVShow) => React.ReactNode;
  onRemove?: (id: number) => void;
  isInWatchlist?: boolean;
}

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: var(--spacing-4);
  padding: var(--spacing-4);
  
  @media (max-width: 1200px) {
    grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
    gap: var(--spacing-3);
  }
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: var(--spacing-3);
    padding: var(--spacing-2);
  }
  
  @media (max-width: 480px) {
    grid-template-columns: repeat(2, 1fr);
    gap: var(--spacing-2);
    padding: var(--spacing-2);
  }
`;

const NoResults = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  width: 100%;
  color: var(--text-secondary);
  font-size: 1.2rem;
  text-align: center;
  background: var(--background-secondary);
  border-radius: 8px;
  padding: var(--spacing-4);
  
  @media (max-width: 768px) {
    height: 150px;
    font-size: 1rem;
    padding: var(--spacing-3);
    border-radius: 6px;
  }
  
  @media (max-width: 480px) {
    height: 120px;
    font-size: 0.9rem;
    padding: var(--spacing-2);
  }
`;

export const MediaGrid = ({ items, type, showRemoveButton, renderItem, onRemove, isInWatchlist }: MediaGridProps) => {
  const dispatch = useDispatch();
  const watchlist = useSelector((state: RootState) => state.media?.watchlist) || { movies: [], shows: [] };

  const handleToggleWatchlist = (id: number) => {
    if (type === "movie") {
      dispatch(toggleMovieWatchlist(id));
    } else {
      dispatch(toggleShowWatchlist(id));
    }
  };

  const isItemInWatchlist = (id: number): boolean => {
    if (!watchlist) return false;
    
    if (type === "movie") {
      return watchlist.movies.some((item) => item.id === id);
    }
    return watchlist.shows.some((item) => item.id === id);
  };

  if (!items || items.length === 0) {
    return (
      <NoResults>
        Aucun résultat trouvé
      </NoResults>
    );
  }

  return (
    <Grid>
      {items.map((item) => {
        if (renderItem) {
          return renderItem(item);
        }

        return (
          <MediaCard
            key={item.id}
            item={item}
            type={type}
            onToggleWatchlist={() => handleToggleWatchlist(item.id)}
            isInWatchlist={isInWatchlist ? isInWatchlist : isItemInWatchlist(item.id)}
            showRemoveButton={showRemoveButton}
            onRemove={onRemove ? () => onRemove(item.id) : undefined}
            className="notranslate"
          />
        );
      })}
    </Grid>
  );
};
